"use client"

import styled from "@emotion/styled"
import { extractProperty, getNonUmbracoUrl } from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { Button, Typography } from "@mui/material"
import Image from "next/image"
import Link from "next/link"
import Container from "./Container"
import { Desktop, Mobile } from "./MediaQueries"

const StyledContainer = styled.div<{ flipSide: boolean; mobileGrey: boolean }>`
  width: 100%;
  padding-top: ${({ flipSide }) => (flipSide ? "0" : "64px")};
  padding-bottom: 64px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 112px 0;
  }
  ${(props) =>
    props.theme.breakpoints.down("md") &&
    props.mobileGrey &&
    `
      background-color: #f3f3f3;
    `}
`

/* The image column is keyed off the desktop image specifically: the second track
 * only exists from md up, so a block with a mobile-only image must not reserve
 * 518px of empty space there. */
const StyledGrid = styled.div<{ flipSide: boolean; hasDesktopImage: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: ${({ flipSide, hasDesktopImage }) =>
      !hasDesktopImage ? "1fr" : flipSide ? "518px 1fr" : "1fr 518px"};
    gap: 103px;
  }
`

const ContentBox = styled.div<{ flipSide: boolean; hasImage: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  ${({ flipSide, hasImage }) =>
    flipSide &&
    hasImage &&
    `
      order: 1;
    `}
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
`

/*
 * Desktop and Mobile are CSS display wrappers, not conditional renders, so a
 * block configured with only one of the two images would otherwise leave an
 * empty fixed-height box on the other breakpoint — the same dead space END-275
 * removes. The container hides itself wherever it has nothing to show.
 */
const ImageContainer = styled.div<{
  hasDesktopImage: boolean
  hasMobileImage: boolean
}>`
  width: 100%;
  height: 348px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  display: ${({ hasMobileImage }) => (hasMobileImage ? "block" : "none")};
  ${({ theme }) => theme.breakpoints.up("md")} {
    height: 518px;
    display: ${({ hasDesktopImage }) => (hasDesktopImage ? "block" : "none")};
  }
`

const StyledTypography = styled(Typography)<{ component?: React.ElementType }>`
  p {
    margin: 0;
  }
`

const StripsContainer = styled.div`
  display: flex;
  padding-top: 16px;
  flex-direction: column;
  gap: 12px;
  position: relative;
  z-index: 0;
`

const StripRow = styled.div`
  display: flex;
  gap: 25px;
  flex-wrap: wrap;
`

const StyledButton = styled(Button)`
  width: 100%;
  padding: 0 !important;
  ${({ theme }) => theme.breakpoints.up("md")} {
    max-width: 249px;
  }
`

const IconRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  row-gap: 16px;
`

const IconItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`

const PackagesRow = styled.div`
  display: flex;
  gap: 24px;
  margin: 24px 0;
  width: 100%;
`
const OperatingIconsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 1fr 1fr;
  }
`
const PackageItem = styled.div`
  height: 90px;
  padding: 16px 8px;
  min-width: 95px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    height: 132px;
    min-width: 123px;
    padding: 24px 12px;
  }

  border-radius: 8px;
  border: 1px solid #ece9e9;
  background: #f3f3f3;
  display: flex;

  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`

const StyledIconTypography = styled(Typography)`
  font-size: 16px !important;
  line-height: 140% !important;
`

export default function TextImageBlock({
  data,
  blockIndex,
}: {
  data: any
  blockIndex?: number
}) {
  const title = extractProperty(data, "title")
  const subTitle = extractProperty(data, "subTitle")
  const content = extractProperty(data, "content")
  const button = extractProperty(data, "button")
  const buttonText = button?.[0]?.name
  const buttonUrl = button?.[0]?.url
  const imageForDesktop = extractProperty(data, "imageForDesktop")
  const imageForMobile = extractProperty(data, "imageForMobile")
  const image = extractProperty(data, "image")
  const desktopImageUrl = getUmbracoMediaUrl(imageForDesktop || image)
  const mobileImageUrl = getUmbracoMediaUrl(imageForMobile || image)
  // No image picked in Umbraco means no image element and no image column at all,
  // otherwise the empty src renders a broken-image glyph next to a dead column
  // (END-275). Tracked per breakpoint, because the grid only reserves the image
  // column from md up and each breakpoint can be configured independently.
  const hasDesktopImage = Boolean(desktopImageUrl)
  const hasMobileImage = Boolean(mobileImageUrl)
  const hasImage = hasDesktopImage || hasMobileImage
  const flipSide = extractProperty(data, "flipSide") as boolean
  const customersStrip = extractProperty(data, "customersStrip")
  const titleStrip = extractProperty(customersStrip?.[0], "title")
  const logosStrip = extractProperty(customersStrip?.[0], "logosStrip", true)
  const iconsList = extractProperty(data, "packagesItems")?.[0]
  const iconsTitle = extractProperty(iconsList, "title")
  const logosArray = extractProperty(iconsList, "logosStrip") || []
  const logos =
    logosArray?.map((el: any) => {
      return {
        text: extractProperty(el, "text"),
        icon: extractProperty(el, "icon"),
      }
    }) || []
  const iconsOperatingLeasingArray = extractProperty(data, "icons") || []
  const iconsOperatingLeasing = iconsOperatingLeasingArray?.map((el: any) => {
    return {
      icon: extractProperty(el, "icon"),
      text: extractProperty(el, "text"),
    }
  })
  const packagesListArray = extractProperty(data, "packagesItems1") || []
  const packagesList =
    packagesListArray?.map((el: any) => {
      return {
        icon: extractProperty(el, "icon"),
        text: extractProperty(el, "text"),
      }
    }) || []
  return (
    <StyledContainer flipSide={flipSide} mobileGrey={blockIndex === 3}>
      <Container paddingMobile>
        <StyledGrid flipSide={flipSide} hasDesktopImage={hasDesktopImage}>
          <ContentBox flipSide={flipSide} hasImage={hasImage}>
            <TextBox>
              <Typography variant="h2">{title}</Typography>
              {subTitle && (
                <Typography variant="h3" style={{ marginTop: "-8px" }}>
                  {subTitle}
                </Typography>
              )}
              <StyledTypography
                variant="body2"
                component="div"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </TextBox>
            {iconsTitle && <Typography variant="h3">{iconsTitle}</Typography>}
            {logos.length > 0 && (
              <IconRow>
                {logos.map((el: any) => (
                  <IconItem key={el.text}>
                    {getUmbracoMediaUrl(el.icon) && (
                      <Image
                        src={getUmbracoMediaUrl(el.icon)}
                        alt=""
                        aria-hidden="true"
                        width={16}
                        height={16}
                      />
                    )}
                    <StyledIconTypography variant="body2">
                      {el.text}
                    </StyledIconTypography>
                  </IconItem>
                ))}
              </IconRow>
            )}
            {iconsOperatingLeasing.length > 0 && (
              <OperatingIconsGrid>
                {iconsOperatingLeasing.map((el: any) => (
                  <PackageItem key={el.text}>
                    {getUmbracoMediaUrl(el.icon) && (
                      <>
                        <Desktop centerHorizontally>
                          <Image
                            src={getUmbracoMediaUrl(el.icon)}
                            alt=""
                            aria-hidden="true"
                            width={43}
                            height={40}
                          />
                        </Desktop>
                        <Mobile centerHorizontally>
                          <Image
                            src={getUmbracoMediaUrl(el.icon)}
                            alt=""
                            aria-hidden="true"
                            width={34}
                            height={32}
                          />
                        </Mobile>
                      </>
                    )}
                    <Typography variant="body3">{el.text}</Typography>
                  </PackageItem>
                ))}
              </OperatingIconsGrid>
            )}
            {packagesList.length > 0 && (
              <PackagesRow>
                {packagesList.map((el: any) => (
                  <PackageItem key={el.text}>
                    {getUmbracoMediaUrl(el.icon) && (
                      <Image
                        src={getUmbracoMediaUrl(el.icon)}
                        alt=""
                        aria-hidden="true"
                        width={24}
                        height={24}
                      />
                    )}
                    <Typography variant="body3">{el.text}</Typography>
                  </PackageItem>
                ))}
              </PackagesRow>
            )}
            {buttonText && (
              <Desktop>
                <StyledButton
                  LinkComponent={Link}
                  variant="primary"
                  size="large"
                  href={getNonUmbracoUrl(buttonUrl)}
                >
                  {buttonText}
                </StyledButton>
              </Desktop>
            )}
            {blockIndex !== 1 && buttonText ? (
              <Mobile>
                <StyledButton
                  LinkComponent={Link}
                  variant="primary"
                  size="large"
                  href={getNonUmbracoUrl(buttonUrl)}
                >
                  {buttonText}
                </StyledButton>
              </Mobile>
            ) : null}

            {titleStrip && (
              <StripsContainer>
                <Typography variant="body3">{titleStrip}</Typography>
                <StripRow>
                  {logosStrip?.map((el: any, i: number) =>
                    getUmbracoMediaUrl(el.url) ? (
                      <Image
                        key={i}
                        src={getUmbracoMediaUrl(el.url)}
                        alt={el.altText || ""}
                        width={48}
                        height={32}
                      />
                    ) : null,
                  )}
                </StripRow>
              </StripsContainer>
            )}
          </ContentBox>
          {hasImage && (
            <ImageContainer
              hasDesktopImage={hasDesktopImage}
              hasMobileImage={hasMobileImage}
            >
              {desktopImageUrl && (
                <Desktop>
                  <Image
                    src={desktopImageUrl}
                    alt={title || ""}
                    fill
                    sizes="(min-width: 900px) 50vw, 100vw"
                  />
                </Desktop>
              )}
              {mobileImageUrl && (
                <Mobile>
                  <Image
                    src={mobileImageUrl}
                    alt={title || ""}
                    fill
                    sizes="(min-width: 900px) 50vw, 100vw"
                  />
                </Mobile>
              )}
            </ImageContainer>
          )}
          {blockIndex === 1 && buttonText ? (
            <Mobile>
              <StyledButton
                LinkComponent={Link}
                variant="primary"
                size="large"
                href={getNonUmbracoUrl(buttonUrl)}
              >
                {buttonText}
              </StyledButton>
            </Mobile>
          ) : null}
        </StyledGrid>
      </Container>
    </StyledContainer>
  )
}
